import { ReactFlowProvider } from '@xyflow/react';
import { ZfBox } from '@zafire/react';
import type { FunctionComponent, ReactNode, RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { ClientPackageChapterName } from '../../enums';
import type { AvailableLanguage } from '../../i18n/i18nextInit';
import type { IChapterImageDom } from '../../interfaces/package';
import { getYwuShadowRoot } from '../../utils/utils';
import type { IClientPackageFamilyTreeRef } from './scenarios/ClientPackageFamilyTree';

/** Avoid capturing before React Flow has painted edges (multi-node trees need edge paths in the DOM). */
function isFamilyTreeViewportReadyForCapture(viewport: HTMLElement): boolean {
    const nodeCount = viewport.querySelectorAll('.react-flow__node').length;
    const edgePathCount = viewport.querySelectorAll('.react-flow__edge-path').length;

    if (nodeCount === 0) return false;

    return !(nodeCount > 1 && edgePathCount === 0);
}

/**
 * The liquidity chart depends on async React Query data; while loading it renders a spinner. Only
 * register the DOM for capture once Highcharts has painted the waterfall (svg root + at least one
 * point), otherwise the exported image can be a loading spinner.
 */
function isHighchartsReadyForCapture(dom: HTMLElement): boolean {
    return !!dom.querySelector('.highcharts-root') && dom.querySelectorAll('.highcharts-point').length > 0;
}

// PNG size in ecrion template: 270*150 mm, keep ratio
const pngHeight = 1000;

type ClientPackageRef = IClientPackageFamilyTreeRef;

export interface IExporterProps {
    onImageRendering: (imagePromise: IChapterImageDom) => void;
    componentToRender: ReactNode | ((ref: RefObject<ClientPackageRef | null>) => ReactNode);
    chapterName: ClientPackageChapterName;
    width?: number;
    height?: number;
    language: AvailableLanguage;
}

export const Exporter: FunctionComponent<IExporterProps> = ({ onImageRendering, componentToRender, chapterName, language, ...props }) => {
    const { i18n } = useTranslation();

    const height = props.height || pngHeight;
    const pngWidth = chapterName === ClientPackageChapterName.PROJECTION ? 2500 : 1800;
    const width = props.width || pngWidth;
    const domElementId = `componentToRender-${chapterName}`;

    const familyTreeRef = useRef<IClientPackageFamilyTreeRef>(null);

    const i18nExporterInstance = useMemo(() => i18n.cloneInstance({ lng: language }), [i18n, language]);

    const getDomElement = useCallback((id?: string, className?: string) => {
        if (!id && !className) return null;

        let domElement = id ? document.getElementById(id) : document.querySelector<HTMLElement>(className!);
        const shadowRoot = getYwuShadowRoot();
        if (!domElement && shadowRoot) {
            domElement = shadowRoot.querySelector<HTMLElement>(id ? `#${id}` : className!);
        }

        return domElement;
    }, []);

    useEffect(() => {
        let cancelled = false;

        if (chapterName === ClientPackageChapterName.LIQUIDITY) {
            const interval = setInterval(() => {
                const domElement = getDomElement(domElementId);

                if (domElement && !cancelled && isHighchartsReadyForCapture(domElement)) {
                    onImageRendering({ chapterName, dom: domElement, options: undefined });
                    clearInterval(interval);
                }
            }, 100);

            return () => {
                cancelled = true;
                clearInterval(interval);
            };
        }

        if (chapterName !== ClientPackageChapterName.FAMILY_TREE) {
            const domElement = getDomElement(domElementId);

            if (domElement && !cancelled) onImageRendering({ chapterName, dom: domElement, options: undefined });

            return;
        }

        const interval = setInterval(() => {
            const domElement = getDomElement(undefined, '.react-flow__viewport');

            if (domElement && familyTreeRef.current && !cancelled && isFamilyTreeViewportReadyForCapture(domElement)) {
                requestAnimationFrame(() => {
                    if (cancelled) return;
                    const options = familyTreeRef.current?.getFamilyTreeOptions(width, height);
                    if (options) onImageRendering({ chapterName, dom: domElement, options });
                });
                clearInterval(interval);
            }
        }, 100);

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [domElementId, getDomElement, onImageRendering, chapterName, width, height]);

    return (
        <ReactFlowProvider>
            <I18nextProvider i18n={i18nExporterInstance}>
                <ZfBox style={{ width: '100%', height: '100%', overflow: 'hidden', position: 'relative' }}>
                    <ZfBox style={{ width, height, background: 'white', position: 'absolute' }} id={domElementId}>
                        {typeof componentToRender === 'function' ? componentToRender(familyTreeRef) : componentToRender}
                    </ZfBox>
                </ZfBox>
            </I18nextProvider>
        </ReactFlowProvider>
    );
};
